import { TemplateRef } from '@angular/core';
import { DateTime } from 'luxon';

import { ValueKeys } from '@shared/types/value-keys';

export interface ChartConfig<Data extends object> {
  data: Data[];
  margin?: Partial<ChartMargin>; // defaults to { top: 48, right: 20, bottom: 18, left: 22 }
  leftYAxis?: ChartYAxisConfig<Data>;
  rightYAxis?: ChartYAxisConfig<Data>;
  xAxis: ChartXAxisConfig<Data>;
  grid?: ChartGridConfig;
  pointerPosition?: ChartPointerPositionConfig;
  dataPointClick?: (data: Data, index: number) => void;
  dataPointTemplates?: ChartDataPointTemplate<Data>[];
  xAxisTickTemplate?: ChartXAxisTickTemplate<Data>;
  cursorTooltipTemplate?: ChartCursorTooltipTemplate<Data>;
  charts: ChartTypeConfig<Data>[];
  nowLineConfig?: ChartNowLineConfig;
}

export enum ChartType {
  Bar = 'bar',
  StackedBar = 'stackedBar',
  GroupedBar = 'groupedBar',
  Line = 'line',
  BackgroundRect = 'backgroundRect',
}

export interface ChartMargin {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

export interface ChartXAxisConfig<Data extends object> {
  dataKey: ValueKeys<Data, number | DateTime>;
  label?: string;
  labelTransform?: string;
  tickValues?: number[];
  tickFormat?: (value: number, timeZone?: string) => string;
  edgePaddingRatio?: number; // Fraction of the closest data-point interval to reserve at each edge
  verticalTexts?: boolean;
  hourMinutesTickFormat?: boolean;
  compactDataPointLayout?: ChartCompactDataPointLayout;
}

export interface ChartCompactDataPointLayout {
  barWidth: number;
  gap: number;
}

export interface ChartYAxisConfig<Data extends object> {
  dataKey?: ValueKeys<Data, number>; // Either a dataKey or a dataMapper function must be provided
  dataMapper?: (data: Data, idx: number) => number;
  hidden?: boolean;
  min?: number; // default 0
  max?: number;
  step?: number;
  tickValues?: number[];
  tickClassFn?: (value: number, idx: number) => string;
  maxTickValue?: number;
  label?: string;
  gapBelowLabel?: number; // default 12
  tickFormat?: (value: number) => string;
  axisAndLinesGap?: number; // default 4
}

export interface ChartGridConfig {
  hidden?: boolean;
  zeroBaseline?: boolean;
}

export interface ChartPointerPositionConfig {
  minWidth?: number;
  bgFillColorClass?: string;
  textColorFillClass?: string;
  formatter?: (value: number, timeZone?: string) => string;
}

export interface ChartDataPointTemplateContext<Data extends object> {
  $implicit: Data;
  data: Data;
  index: number;
  x: number;
  y: number;
}

export interface ChartDataPointTemplate<Data extends object> {
  template: TemplateRef<ChartDataPointTemplateContext<Data>>;
  position?: (context: ChartDataPointTemplateContext<Data>) => { x: number; y: number };
}

export interface ChartXAxisTickTemplateContext<Data extends object> {
  $implicit: Data;
  data: Data;
  index: number;
  x: number;
  y: number;
  vertical: boolean;
}

export interface ChartXAxisTickTemplate<Data extends object> {
  template: TemplateRef<ChartXAxisTickTemplateContext<Data>>;
}

export interface ChartCursorTooltipTemplateContext<Data extends object> {
  $implicit: Data;
  data: Data;
  index: number;
  x: number;
}

export interface ChartCursorTooltipTemplate<Data extends object> {
  template: TemplateRef<ChartCursorTooltipTemplateContext<Data>>;
}

export type ChartTypeConfig<Data extends object> =
  | ChartBarConfig<Data>
  | ChartLineConfig<Data>
  | ChartStackedBarConfig<Data>
  | ChartGroupedBarConfig<Data>
  | ChartBackgroundRectConfig<Data>;

export interface TooltipConfig<Data extends object> {
  formatter?: (value: number) => string;
  hidden?: (data: Data, idx: number) => boolean;
  // Provide a fill colour either as a Tailwind class (`fillColorClass`) or an explicit hex
  // (`fillColor`, takes precedence). At least one must be set.
  fillColorClass?: string | ((data: Data, idx: number) => string);
  fillColor?: string | ((data: Data, idx: number) => string);
  textColorClass: string | ((data: Data, idx: number) => string);
}

export interface ChartBaseConfig {
  rightYAxis?: boolean;
}

export interface ChartBarConfig<Data extends object> extends ChartBaseConfig {
  type: ChartType.Bar;
  dataKey: ValueKeys<Data, number | null | undefined>;
  barPadding?: number; // default 2
  maxBarWidth?: number; // default 16
  borderRadius?: number; // default 4
  fillColorClass: string | ((value: Data, idx: number) => string);
  valueLabelFormatter?: (value: number) => string;
  tooltipConfig: TooltipConfig<Data>;
  backgroundFillColorClassFn?: (data: Data) => string | undefined;
}

export interface ChartStackedBarConfig<Data extends object> extends ChartBaseConfig {
  type: ChartType.StackedBar;
  bars: ChartStackedBarBarConfig<Data>[];
  striped?: boolean | ((value: Data, idx: number) => boolean);
  barPadding?: number; // default 2
  maxBarWidth?: number; // default 16
  borderTopRadius?: number; // default 4
  borderBottomRadius?: number; // default 0
}

export interface ChartStackedBarBarConfig<Data extends object> {
  dataKey: ValueKeys<Data, number | null | undefined>;
  // Provide a fill colour either as a Tailwind class (`fillColorClass`) or an explicit hex
  // (`fillColor`, takes precedence). At least one must be set.
  fillColorClass?: string | ((value: Data, idx: number) => string);
  fillColor?: string | ((value: Data, idx: number) => string);
  tooltipConfig: TooltipConfig<Data>;
}

export interface ChartGroupedBarConfig<Data extends object> extends ChartBaseConfig {
  type: ChartType.GroupedBar;
  bars: ChartGroupedBarBarConfig<Data>[];
  groupPadding?: number; // default 4
  barGap?: number; // default 2
  minBarWidth?: number; // default 2
  borderRadius?: number; // default 4
  backgroundFillColorClassFn?: (data: Data) => string | undefined;
}

export interface ChartGroupedBarBarConfig<Data extends object> {
  dataKey?: ValueKeys<Data, number | null | undefined>;
  dataMapper?: (data: Data, idx: number) => number | null;
  fillColorClass: string;
  tooltipConfig: TooltipConfig<Data>;
}

export interface ChartBackgroundRectConfig<Data extends object> extends ChartBaseConfig {
  type: ChartType.BackgroundRect;
  width?: number; // defaults to the x-axis bandwidth
  topOverflow?: number; // defaults to 0
  bottomOverflow?: number; // defaults to 0
  borderRadius?: number;
  backgroundFillColorClassFn: (data: Data) => string | undefined;
}

export interface ChartLineConfig<Data extends object> extends ChartBaseConfig {
  type: ChartType.Line;
  lineType?: ChartLineType; // default ChartLineType.Linear
  spannedAcrossXBandwidth?: boolean;
  dataKey?: ValueKeys<Data, number | null | undefined>;
  dataMapper?: (data: Data, idx: number) => number | null;
  strokeColorClass: string;
  strokeWidth?: number; // default 2
  tooltipConfig: TooltipConfig<Data>;
  gradientConfig?: ChartLineGradientConfig;
}

export enum ChartLineType {
  Linear = 'linear',
  Stepped = 'stepped',
}

export interface ChartLineGradientConfig {
  gradientTextColorClass: string;
}

export interface ChartNowLineConfig {
  now: DateTime;
  offsetXMillis?: number; // default 0
  strokeColorClass?: string; // default 'stroke-foreground-primary-100'
}
